/**
 * Stream Recovery Handler for Frontend
 * Handles recovery events from the backend without disrupting the UI
 */

export interface RecoveryStatus {
  status: 'recovering' | 'recovered' | 'failed';
  message: string;
  timestamp: string;
}

export interface ToolFailureEvent {
  type: 'tool_failure';
  toolCallId: string;
  toolName: string;
  error: string;
  attemptedParameters: any;
  suggestions?: string[];
}

export class StreamRecoveryHandler {
  private recoveryCallbacks: Map<string, (status: RecoveryStatus) => void> = new Map();
  private isRecovering: boolean = false;
  private recoveryStartTime: number | null = null;
  
  /**
   * Register a callback for recovery status updates
   */
  onRecoveryStatus(messageId: string, callback: (status: RecoveryStatus) => void): void {
    this.recoveryCallbacks.set(messageId, callback);
  }
  
  /**
   * Handle recovery status event from SSE
   */
  handleRecoveryStatus(messageId: string, status: RecoveryStatus): void {
    console.log(`🔄 Recovery status for message ${messageId}:`, status);
    
    if (status.status === 'recovering') {
      this.isRecovering = true;
      this.recoveryStartTime = Date.now();
      
      // Show subtle UI indication
      this.showRecoveryIndicator(messageId, status.message);
    } else if (status.status === 'recovered') {
      const recoveryTime = this.recoveryStartTime ? Date.now() - this.recoveryStartTime : 0;
      console.log(`✅ Recovered in ${recoveryTime}ms`);
      
      this.isRecovering = false;
      this.recoveryStartTime = null;
      
      // Hide recovery indicator
      this.hideRecoveryIndicator(messageId);
    } else if (status.status === 'failed') {
      console.error(`❌ Recovery failed for message ${messageId}`);
      this.isRecovering = false;
      
      // Show error but keep existing content
      this.showRecoveryError(messageId, status.message);
    }
    
    // Notify registered callbacks
    const callback = this.recoveryCallbacks.get(messageId);
    if (callback) {
      callback(status);
    }
  }
  
  /**
   * Handle tool failure event (for debugging/monitoring)
   */
  handleToolFailure(event: ToolFailureEvent): void {
    console.warn(`⚠️ Tool failure:`, {
      tool: event.toolName,
      error: event.error,
      suggestions: event.suggestions
    });
    
    // In production, you might want to show this in a debug panel
    // For now, just log it
  }
  
  /**
   * Show recovery indicator in UI
   */
  private showRecoveryIndicator(messageId: string, message: string): void {
    // Find the message element
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    if (!messageElement) return;
    
    // Check if indicator already exists
    let indicator = messageElement.querySelector('.recovery-indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'recovery-indicator';
      messageElement.appendChild(indicator);
    }
    
    // Update indicator content
    indicator.innerHTML = `
      <div class="recovery-content">
        <svg class="recovery-spinner" width="16" height="16" viewBox="0 0 24 24">
          <circle class="spinner-track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" opacity="0.3"/>
          <path class="spinner-path" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round">
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 12 12"
              to="360 12 12"
              dur="1s"
              repeatCount="indefinite"/>
          </path>
        </svg>
        <span class="recovery-message">${message || 'Resuming...'}</span>
      </div>
    `;
    
    // Add CSS if not already present
    this.injectRecoveryStyles();
  }
  
  /**
   * Hide recovery indicator
   */
  private hideRecoveryIndicator(messageId: string): void {
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    if (!messageElement) return;
    
    const indicator = messageElement.querySelector('.recovery-indicator');
    if (indicator) {
      // Fade out animation
      indicator.classList.add('fade-out');
      setTimeout(() => indicator.remove(), 300);
    }
  }
  
  /**
   * Show recovery error
   */
  private showRecoveryError(messageId: string, errorMessage: string): void {
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    if (!messageElement) return;
    
    // Add error indicator but keep content
    const errorDiv = document.createElement('div');
    errorDiv.className = 'recovery-error';
    errorDiv.innerHTML = `
      <div class="error-content">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 8v4m0 4h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <span>${errorMessage || 'Connection interrupted'}</span>
        <button class="retry-button" onclick="this.parentElement.parentElement.remove()">Dismiss</button>
      </div>
    `;
    
    messageElement.appendChild(errorDiv);
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      errorDiv.classList.add('fade-out');
      setTimeout(() => errorDiv.remove(), 300);
    }, 5000);
  }
  
  /**
   * Inject recovery-related CSS styles
   */
  private injectRecoveryStyles(): void {
    if (document.getElementById('recovery-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'recovery-styles';
    style.textContent = `
      .recovery-indicator {
        position: absolute;
        top: 8px;
        right: 8px;
        background: rgba(59, 130, 246, 0.1);
        border: 1px solid rgba(59, 130, 246, 0.3);
        border-radius: 6px;
        padding: 4px 8px;
        font-size: 12px;
        color: rgb(59, 130, 246);
        z-index: 10;
        animation: fadeIn 0.3s ease-in;
      }
      
      .recovery-content {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      
      .recovery-spinner {
        animation: spin 1s linear infinite;
      }
      
      .recovery-error {
        margin-top: 8px;
        padding: 8px 12px;
        background: rgba(239, 68, 68, 0.1);
        border: 1px solid rgba(239, 68, 68, 0.3);
        border-radius: 6px;
        color: rgb(239, 68, 68);
        font-size: 14px;
        animation: fadeIn 0.3s ease-in;
      }
      
      .error-content {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      
      .retry-button {
        margin-left: auto;
        padding: 2px 8px;
        background: transparent;
        border: 1px solid currentColor;
        border-radius: 4px;
        color: inherit;
        cursor: pointer;
        font-size: 12px;
      }
      
      .retry-button:hover {
        background: rgba(239, 68, 68, 0.1);
      }
      
      .fade-out {
        animation: fadeOut 0.3s ease-out forwards;
      }
      
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      @keyframes fadeOut {
        from { opacity: 1; transform: translateY(0); }
        to { opacity: 0; transform: translateY(-4px); }
      }
      
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      /* Dark mode support */
      @media (prefers-color-scheme: dark) {
        .recovery-indicator {
          background: rgba(59, 130, 246, 0.15);
          border-color: rgba(59, 130, 246, 0.4);
        }
        
        .recovery-error {
          background: rgba(239, 68, 68, 0.15);
          border-color: rgba(239, 68, 68, 0.4);
        }
      }
    `;
    
    document.head.appendChild(style);
  }
  
  /**
   * Check if currently recovering
   */
  isCurrentlyRecovering(): boolean {
    return this.isRecovering;
  }
  
  /**
   * Clean up callbacks for a message
   */
  cleanup(messageId: string): void {
    this.recoveryCallbacks.delete(messageId);
    this.hideRecoveryIndicator(messageId);
  }
}

// Export singleton instance
export const streamRecovery = new StreamRecoveryHandler();